// Lista de compensações (na ficha do jogador e no painel "por aprovar"), com aprovar/recusar para quem pode.
import { useApi } from "@ecliptica/api-client/react";
import { useTranslation } from "@ecliptica/i18n";
import { Badge, Button } from "@ecliptica/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { formatDate } from "../../lib/format";
import { useCan } from "../auth/RequireStaff";
import { ActionDialog } from "./ActionDialog";

export type CompensationItem = {
  id: number;
  userId: string;
  userName: string | null;
  coins: number;
  dust: number;
  packs: number;
  reason: string;
  status: "pending_approval" | "approved" | "rejected" | "applied";
  requestedByName: string | null;
  decidedByName: string | null;
  decisionReason: string | null;
  createdAt: string;
};

const VARIANT = { pending_approval: "warning", approved: "success", rejected: "destructive", applied: "secondary" } as const;

export function CompensationList({ items, showUser = false }: { items: CompensationItem[]; showUser?: boolean }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const [deciding, setDeciding] = useState<{ id: number; approve: boolean } | null>(null);

  const amounts = (c: CompensationItem) =>
    [c.coins && `${c.coins} ${t("users.comp.coins")}`, c.dust && `${c.dust} ${t("users.comp.dust")}`, c.packs && `${c.packs} ${t("users.comp.packs")}`].filter(Boolean).join(" · ");

  return (
    <>
      <ul className="flex flex-col gap-2">
        {items.map((c) => (
          <li key={c.id} className="flex flex-wrap items-start gap-3 rounded-md border p-3 text-sm">
            <div className="min-w-0 flex-1">
              <p className="font-semibold">
                {amounts(c)}
                {showUser && (
                  <>
                    {" → "}
                    <Link to={`/users/${c.userId}`} className="text-gold-bright hover:underline">
                      {c.userName}
                    </Link>
                  </>
                )}
              </p>
              <p className="text-muted-foreground">{c.reason}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {formatDate(c.createdAt, i18n.language)} · {t("users.comp.by", { name: c.requestedByName ?? "—" })}
                {c.decidedByName && c.status !== "approved" ? ` · ${t("users.comp.decidedBy", { name: c.decidedByName })}` : ""}
                {c.decisionReason ? ` — ${c.decisionReason}` : ""}
              </p>
            </div>
            <Badge variant={VARIANT[c.status]}>{t(`users.comp.status.${c.status}`)}</Badge>
            {c.status === "pending_approval" && can("compensations.approve") && (
              <div className="flex gap-2">
                <Button size="sm" variant="primary" onClick={() => setDeciding({ id: c.id, approve: true })}>
                  {t("users.comp.approve")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDeciding({ id: c.id, approve: false })}>
                  {t("users.comp.reject")}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <ActionDialog
        open={!!deciding}
        onOpenChange={(o) => !o && setDeciding(null)}
        title={deciding?.approve ? t("users.comp.approve") : t("users.comp.reject")}
        destructive={!deciding?.approve}
        onConfirm={async (reason) => {
          const res = await api.v1.admin.compensations[":cid"].decision.$post({ param: { cid: String(deciding!.id) }, json: { approve: deciding!.approve, reason } });
          if (res.ok) {
            await qc.invalidateQueries({ queryKey: ["compensations"] });
            await qc.invalidateQueries({ queryKey: ["user"] });
          }
          return res;
        }}
      />
    </>
  );
}
